import React from 'react'
import { createPortal } from 'react-dom'
import Invoice from './Invoice'

const PrintableInvoice = ({ order, isOpen, onClose }) => {
  const handlePrint = () => {
    // Create a new window for printing
    const printWindow = window.open('', '_blank', 'width=800,height=600')
    
    if (!printWindow) {
      alert('Please allow popups to print the invoice')
      return
    }

    // Get the invoice HTML
    const invoiceElement = document.getElementById('printable-invoice')
    if (!invoiceElement) return

    // Write the content to the new window
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Invoice #${order?.order_number || ''}</title>
          <meta charset="utf-8">
          <meta name="viewport" content="width=device-width, initial-scale=1">
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            body {
              font-family: Arial, sans-serif;
              line-height: 1.6;
              color: #333;
              background: white;
            }
            .invoice-container {
              max-width: none !important;
              box-shadow: none !important;
              border: none !important;
              padding: 20px;
            }
            table {
              border-collapse: collapse;
              width: 100%;
            }
            th, td {
              border: 1px solid #ddd;
              padding: 8px;
              text-align: left;
            }
            th {
              background-color: #f5f5f5;
              font-weight: bold;
            }
            .text-right {
              text-align: right;
            }
            .text-center {
              text-align: center;
            }
            .grid {
              display: grid;
              gap: 1.5rem;
            }
            .grid-cols-2 {
              grid-template-columns: repeat(2, 1fr);
            }
            .bg-gray-50 {
              background-color: #f9fafb;
              padding: 1rem;
              border-radius: 0.5rem;
            }
            .space-y-2 > * + * {
              margin-top: 0.5rem;
            }
            .flex {
              display: flex;
            }
            .justify-between {
              justify-content: space-between;
            }
            .items-center {
              align-items: center;
            }
            .items-start {
              align-items: flex-start;
            }
            .space-x-4 > * + * {
              margin-left: 1rem;
            }
            .mb-8 {
              margin-bottom: 2rem;
            }
            .mb-6 {
              margin-bottom: 1.5rem;
            }
            .mb-4 {
              margin-bottom: 1rem;
            }
            .mb-3 {
              margin-bottom: 0.75rem;
            }
            .mb-2 {
              margin-bottom: 0.5rem;
            }
            .mt-2 {
              margin-top: 0.5rem;
            }
            .pt-4 {
              padding-top: 1rem;
            }
            .text-2xl {
              font-size: 1.5rem;
              line-height: 2rem;
            }
            .text-xl {
              font-size: 1.25rem;
              line-height: 1.75rem;
            }
            .text-lg {
              font-size: 1.125rem;
              line-height: 1.75rem;
            }
            .text-sm {
              font-size: 0.875rem;
              line-height: 1.25rem;
            }
            .text-xs {
              font-size: 0.75rem;
              line-height: 1rem;
            }
            .font-bold {
              font-weight: 700;
            }
            .font-semibold {
              font-weight: 600;
            }
            .font-medium {
              font-weight: 500;
            }
            .text-gray-900 {
              color: #111827;
            }
            .text-gray-700 {
              color: #374151;
            }
            .text-gray-600 {
              color: #4b5563;
            }
            .text-red-600 {
              color: #dc2626;
            }
            .border-t {
              border-top: 1px solid #d1d5db;
            }
            .w-full {
              width: 100%;
            }
            .max-w-md {
              max-width: 28rem;
            }
            .w-16 {
              width: 4rem;
            }
            .h-16 {
              height: 4rem;
            }
            .object-contain {
              object-fit: contain;
            }
            .capitalize {
              text-transform: capitalize;
            }
            .px-2 {
              padding-left: 0.5rem;
              padding-right: 0.5rem;
            }
            .py-1 {
              padding-top: 0.25rem;
              padding-bottom: 0.25rem;
            }
            .px-4 {
              padding-left: 1rem;
              padding-right: 1rem;
            }
            .py-3 {
              padding-top: 0.75rem;
              padding-bottom: 0.75rem;
            }
            .p-4 {
              padding: 1rem;
            }
            .rounded-lg {
              border-radius: 0.5rem;
            }
            .rounded-full {
              border-radius: 9999px;
            }
            .bg-green-100 {
              background-color: #dcfce7;
            }
            .text-green-800 {
              color: #166534;
            }
            .bg-yellow-100 {
              background-color: #fef3c7;
            }
            .text-yellow-800 {
              color: #92400e;
            }
            .bg-red-100 {
              background-color: #fee2e2;
            }
            .text-red-800 {
              color: #991b1b;
            }
            .bg-gray-100 {
              background-color: #f3f4f6;
            }
            .text-gray-800 {
              color: #1f2937;
            }
            .overflow-x-auto {
              overflow-x: auto;
            }
            .hover\\:bg-gray-50:hover {
              background-color: #f9fafb;
            }
            .border-gray-300 {
              border-color: #d1d5db;
            }
            hr {
              border: 0;
              border-top: 1px solid #d1d5db;
              margin: 0.5rem 0;
            }
            
            @media print {
              body {
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
              .invoice-container {
                margin: 0;
                padding: 0;
              }
            }
          </style>
        </head>
        <body>
          ${invoiceElement.innerHTML}
        </body>
      </html>
    `)

    printWindow.document.close()
    
    // Wait for content to load then print
    printWindow.onload = () => {
      setTimeout(() => {
        printWindow.print()
        printWindow.close()
      }, 100)
    }
  }

  if (!isOpen) return null

  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            Invoice Preview - #{order?.order_number}
          </h2>
          <div className="flex space-x-3">
            <button
              onClick={handlePrint}
              className="bg-secondary text-white px-4 py-2 rounded-lg hover:bg-secondary-700 transition-colors flex items-center space-x-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
              </svg>
              <span>Print</span>
            </button>
            <button
              onClick={onClose}
              className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
            >
              Close
            </button>
          </div>
        </div>

        {/* Invoice Content */}
        <div className="p-6">
          <div id="printable-invoice">
            <Invoice order={order} isPrintMode={true} />
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}

export default PrintableInvoice
